import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FileText,
  Search,
  Plus,
  Copy,
  Edit,
  Trash2,
  Sparkles,
  Tag,
  Loader2,
  X,
  Check,
  ChevronRight,
  Megaphone,
  GraduationCap,
  MessageCircle,
  Bell,
  Calendar,
  Gift,
  Lightbulb,
  Package
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import DashboardLayout from '../components/DashboardLayout';
import { api, isUpgradeError } from '../context/AuthContext';
import { toast } from 'sonner';

const categoryIcons = {
  promotional: Megaphone,
  educational: GraduationCap,
  engagement: MessageCircle,
  announcement: Bell,
  seasonal: Calendar,
  product: Package,
  tips: Lightbulb,
  event: Calendar,
  custom: FileText
};

const categoryColors = {
  promotional: 'bg-orange-100 text-orange-700',
  educational: 'bg-blue-100 text-blue-700',
  engagement: 'bg-pink-100 text-pink-700',
  announcement: 'bg-purple-100 text-purple-700',
  seasonal: 'bg-green-100 text-green-700',
  product: 'bg-indigo-100 text-indigo-700',
  tips: 'bg-yellow-100 text-yellow-700',
  event: 'bg-teal-100 text-teal-700',
  custom: 'bg-slate-100 text-slate-700'
};

const TemplatesPage = () => {
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showUseDialog, setShowUseDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variables, setVariables] = useState({});
  const [newTemplate, setNewTemplate] = useState({
    name: '',
    category: 'custom',
    content: '',
    platforms: [],
    hashtags: []
  });

  useEffect(() => {
    loadData();
  }, [selectedCategory]);

  const loadData = async () => {
    try {
      const [templatesRes, categoriesRes] = await Promise.all([
        api.get('/templates/', { params: selectedCategory !== 'all' ? { category: selectedCategory } : {} }),
        api.get('/templates/categories')
      ]);
      setTemplates(templatesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      toast.error('Failed to load templates');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTemplate = async () => {
    if (!newTemplate.name || !newTemplate.content) {
      toast.error('Please fill in template name and content');
      return;
    }

    try {
      await api.post('/templates/', newTemplate);
      toast.success('Template created!');
      setShowCreateDialog(false);
      setNewTemplate({ name: '', category: 'custom', content: '', platforms: [], hashtags: [] });
      loadData();
    } catch (error) {
      if (!isUpgradeError(error)) toast.error('Failed to create template');
    }
  };

  const handleUseTemplate = async () => {
    if (!selectedTemplate) return;

    try {
      const response = await api.post(`/templates/${selectedTemplate.template_id}/use`, { variables });
      
      // Navigate to post composer with pre-filled content
      navigate('/posts/new', {
        state: {
          content: response.data.rendered_content,
          hashtags: response.data.hashtags,
          platforms: response.data.platforms
        }
      });
      
      toast.success('Template applied!');
    } catch (error) {
      toast.error('Failed to use template');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!confirm('Are you sure you want to delete this template?')) return;

    try {
      await api.delete(`/templates/${templateId}`);
      toast.success('Template deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };

  const openUseDialog = (template) => {
    setSelectedTemplate(template);
    const vars = {};
    template.variables?.forEach(v => { vars[v] = ''; });
    setVariables(vars);
    setShowUseDialog(true);
  };

  const filteredTemplates = templates.filter(t =>
    t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const renderTemplateCard = (template) => {
    const IconComponent = categoryIcons[template.category] || FileText;
    
    return (
      <Card 
        key={template.template_id} 
        className="hover:shadow-md transition-shadow cursor-pointer group"
        data-testid={`template-card-${template.template_id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className={`p-2 rounded-lg ${categoryColors[template.category] || categoryColors.custom}`}>
                <IconComponent className="w-4 h-4" />
              </div>
              <div>
                <h3 className="font-medium text-sm">{template.name}</h3>
                <Badge variant="secondary" className="text-xs mt-1">
                  {template.category}
                </Badge>
              </div>
            </div>
            {template.is_custom && (
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteTemplate(template.template_id);
                }}>
                  <Trash2 className="w-3.5 h-3.5 text-red-500" />
                </Button>
              </div>
            )}
          </div>
          
          <p className="text-xs text-slate-600 line-clamp-3 mb-3">
            {template.content.substring(0, 150)}...
          </p>
          
          <div className="flex items-center justify-between">
            <div className="flex gap-1 flex-wrap">
              {template.platforms?.slice(0, 3).map(p => (
                <Badge key={p} variant="outline" className="text-[10px] px-1.5 py-0">
                  {p}
                </Badge>
              ))}
            </div>
            <Button 
              size="sm" 
              variant="ghost" 
              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
              onClick={() => openUseDialog(template)}
            >
              Use <ChevronRight className="w-3 h-3 ml-1" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Content Templates</h1>
            <p className="text-slate-500 mt-1">Pre-built templates to speed up your content creation</p>
          </div>
          <Button onClick={() => setShowCreateDialog(true)} className="gap-2" data-testid="create-template-btn">
            <Plus className="w-4 h-4" />
            Create Template
          </Button>
        </div>

        {/* Search & Filter */}
        <div className="flex gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="template-search"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.name} value={cat.name}>
                  {cat.icons} {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)} ({cat.system_count})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Category Pills */}
        <div className="flex gap-2 flex-wrap">
          <Button
            variant={selectedCategory === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedCategory('all')}
          >
            All
          </Button>
          {categories.map(cat => {
            const IconComponent = categoryIcons[cat.name] || FileText;
            return (
              <Button
                key={cat.name}
                variant={selectedCategory === cat.name ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(cat.name)}
                className="gap-1"
              >
                <IconComponent className="w-3.5 h-3.5" />
                {cat.name.charAt(0).toUpperCase() + cat.name.slice(1)}
              </Button>
            );
          })}
        </div>

        {/* Templates Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTemplates.map(renderTemplateCard)}
        </div>

        {filteredTemplates.length === 0 && (
          <div className="text-center py-12">
            <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">No templates found</p>
          </div>
        )}

        {/* Create Template Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Create Custom Template</DialogTitle>
              <DialogDescription>
                Create a reusable template for your posts
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Template Name</Label>
                <Input
                  value={newTemplate.name}
                  onChange={(e) => setNewTemplate({ ...newTemplate, name: e.target.value })}
                  placeholder="e.g., Weekly Newsletter"
                  className="mt-1"
                />
              </div>
              <div>
                <Label>Category</Label>
                <Select
                  value={newTemplate.category}
                  onValueChange={(v) => setNewTemplate({ ...newTemplate, category: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="promotional">Promotional</SelectItem>
                    <SelectItem value="educational">Educational</SelectItem>
                    <SelectItem value="engagement">Engagement</SelectItem>
                    <SelectItem value="announcement">Announcement</SelectItem>
                    <SelectItem value="seasonal">Seasonal</SelectItem>
                    <SelectItem value="product">Product</SelectItem>
                    <SelectItem value="tips">Tips</SelectItem>
                    <SelectItem value="event">Event</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Content</Label>
                <Textarea
                  value={newTemplate.content}
                  onChange={(e) => setNewTemplate({ ...newTemplate, content: e.target.value })}
                  placeholder="Write your template content. Use {{variable}} for placeholders."
                  className="mt-1 min-h-[150px]"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Tip: Use {"{{variable}}"} syntax for dynamic content
                </p>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreateTemplate}>
                  Create Template
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Use Template Dialog */}
        <Dialog open={showUseDialog} onOpenChange={setShowUseDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Use Template: {selectedTemplate?.name}</DialogTitle>
              <DialogDescription>
                Fill in the variables to customize your content
              </DialogDescription>
            </DialogHeader>
            {selectedTemplate && (
              <div className="space-y-4">
                {selectedTemplate.variables?.length > 0 ? (
                  <>
                    <div className="space-y-3">
                      {selectedTemplate.variables.map(v => (
                        <div key={v}>
                          <Label className="capitalize">{v.replace(/_/g, ' ')}</Label>
                          <Input
                            value={variables[v] || ''}
                            onChange={(e) => setVariables({ ...variables, [v]: e.target.value })}
                            placeholder={`Enter ${v}...`}
                            className="mt-1"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-slate-500 mb-2">Preview:</p>
                      <p className="text-sm text-slate-700">
                        {selectedTemplate.content.replace(
                          /\{\{(\w+)\}\}/g,
                          (_, key) => variables[key] || `{{${key}}}`
                        ).substring(0, 200)}...
                      </p>
                    </div>
                  </>
                ) : (
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-sm text-slate-700">{selectedTemplate.content}</p>
                  </div>
                )}
                <div className="flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowUseDialog(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleUseTemplate} className="gap-2">
                    <Sparkles className="w-4 h-4" />
                    Create Post
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default TemplatesPage;
